import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save, FileCheck2, Eye, Upload, FileText, X, Lock } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import PageHeader from "../components/PageHeader";
import SearchSelectAddNew from "../components/SearchSelectAddNew";
import { useAuth } from "../context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);
const emptyRow = () => ({ productCode: "", item: "", stdNonStd: "Std", colour: "", qty: 1, unitRate: 0 });

const defaultTerms = {
  price: "As Above", payment: "100% advance before dispatch", cgst: "", deliveryPeriod: "",
  sgst: "", roadPermitNo: "", igst: "Extra @ 18 %", insurance: "", fright: "Extra", installation: "", otherConditions: "",
};

export default function OAForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const can = (p) => isAdmin || perms.includes(p);
  const editing = Boolean(id);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [poBusy, setPoBusy] = useState(false);
  const [custModal, setCustModal] = useState(false);
  const [custForm, setCustForm] = useState({ customerName: "", consigneeAddress: "", deliveryAddress: "", gstNumber: "", consigneeGstNumber: "", contactPerson: "", mobile: "", email: "" });
  const [f, setF] = useState({
    customerId: "", customerName: "", consigneeAddress: "", deliveryAddress: "",
    oaDate: today(), partyPONumber: "", byMail: "", partyPODate: "", gstNumber: "", consigneeGstNumber: "",
    products: [emptyRow()], terms: { ...defaultTerms }, specialInstruction: "",
    preparedBy: "", authorizedSign: "", destination: "", deliveredDetails: "", date: today(),
    billNumber: "", ccDealer: true, ccAccount: true, ccMarketing: true, poFile: null, status: "draft",
  });

  const locked = editing && !isAdmin && ["generated", "completed"].includes(f.status);

  useEffect(() => {
    (async () => {
      const [{ data: cs }, { data: ps }, { data: terms }] = await Promise.all([
        api.get("/customers", { params: { status: "active" } }),
        api.get("/products", { params: { status: "active" } }),
        api.get("/settings/terms"),
      ]);
      setCustomers(cs); setProducts(ps);
      if (editing) {
        const { data: oa } = await api.get(`/oas/${id}`);
        setF({ ...oa, products: oa.products?.length ? oa.products : [emptyRow()], terms: { ...defaultTerms, ...(oa.terms || {}) } });
      } else {
        setF((p) => ({ ...p, terms: { ...defaultTerms, ...terms } }));
      }
    })();
  }, [id]); // eslint-disable-line

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setTerm = (k, v) => setF((p) => ({ ...p, terms: { ...p.terms, [k]: v } }));

  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    if (!c) { set("customerId", ""); return; }
    setF((p) => ({
      ...p, customerId: c.id, customerName: c.customerName,
      consigneeAddress: c.consigneeAddress, deliveryAddress: c.deliveryAddress,
      gstNumber: c.gstNumber, consigneeGstNumber: c.consigneeGstNumber,
    }));
  };

  const setRow = (i, k, v) => setF((p) => {
    const rows = [...p.products];
    rows[i] = { ...rows[i], [k]: v };
    return { ...p, products: rows };
  });

  const pickProduct = (i, code) => {
    const pr = products.find((x) => x.productCode === code);
    if (!pr) return;
    setF((p) => {
      const rows = [...p.products];
      rows[i] = { ...rows[i], productCode: pr.productCode, item: pr.itemName, stdNonStd: pr.stdNonStd, colour: pr.colour, unitRate: pr.defaultUnitRate };
      return { ...p, products: rows };
    });
  };

  const addRow = () => set("products", [...f.products, emptyRow()]);
  const removeRow = (i) => set("products", f.products.filter((_, x) => x !== i));

  const uploadPO = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/po/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("poFile", data);
      toast.success("PO uploaded");
    } catch (err) { toast.error(errMsg(err)); }
    setPoBusy(false);
  };
  const viewPO = async () => {
    try {
      const { data } = await api.get("/po/download", { params: { path: f.poFile.storagePath, mode: "view" }, responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveCustomer = async () => {
    if (!custForm.customerName) return toast.error("Customer name is required.");
    try {
      const { data } = await api.post("/customers", custForm);
      const { data: cs } = await api.get("/customers", { params: { status: "active" } });
      setCustomers(cs);
      setF((p) => ({
        ...p, customerId: data.id, customerName: data.customerName,
        consigneeAddress: data.consigneeAddress, deliveryAddress: data.deliveryAddress,
        gstNumber: data.gstNumber, consigneeGstNumber: data.consigneeGstNumber,
      }));
      setCustModal(false);
      setCustForm({ customerName: "", consigneeAddress: "", deliveryAddress: "", gstNumber: "", consigneeGstNumber: "", contactPerson: "", mobile: "", email: "" });
      toast.success("Customer added");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const net = f.products.reduce((a, p) => a + (Number(p.qty) || 0) * (Number(p.unitRate) || 0), 0);

  const save = async (status, thenPreview) => {
    if (!f.customerName) return toast.error("Please select a customer.");
    if (!f.products.length || f.products.every((p) => !p.item && !p.productCode)) return toast.error("Please add at least one product.");
    setBusy(true);
    try {
      const payload = { ...f, status };
      const { data } = editing ? await api.put(`/oas/${id}`, payload) : await api.post("/oas", payload);
      toast.success(editing ? "OA updated" : `OA ${data.oaNumber} saved`);
      if (thenPreview) navigate(`/oa/${data.id}/preview`);
      else navigate("/oa");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader
        title={editing ? `Edit OA ${f.oaNumber || ""}` : "Create New OA"}
        subtitle="Order Acceptance details"
        actions={
          locked ? (
            <div className="flex items-center gap-2">
              <span className="badge bg-amber-100 text-amber-800 border border-amber-300"><Lock className="h-3.5 w-3.5 mr-1" /> Generated &amp; Locked</span>
              <button className="btn-accent" onClick={() => navigate(`/oa/${id}/preview`)} data-testid="preview-oa-btn"><Eye className="h-4 w-4" /> Preview</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button className="btn-outline" onClick={() => save("draft")} disabled={busy} data-testid="save-draft-btn"><Save className="h-4 w-4" /> Save Draft</button>
              <button className="btn-primary" onClick={() => save("generated")} disabled={busy} data-testid="generate-oa-btn"><FileCheck2 className="h-4 w-4" /> Save &amp; Generate</button>
              <button className="btn-accent" onClick={() => save("generated", true)} disabled={busy} data-testid="preview-oa-btn"><Eye className="h-4 w-4" /> Preview</button>
            </div>
          )
        }
      />
      {locked && (
        <div className="mx-6 lg:mx-8 mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="oa-locked-banner">
          <Lock className="h-4 w-4" /> This OA has been generated and is locked. You can view, print or download it. Contact an administrator to make changes.
        </div>
      )}
      <div className="p-6 lg:p-8 space-y-6 max-w-6xl">
        {/* Consignee / Order details */}
        <div className="card p-6">
          <h3 className="font-head font-semibold text-slate-900 mb-4">Consignee / Order Details</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <label className="label">Customer</label>
                {can("add_customer") && <button type="button" className="btn-ghost btn-sm text-amber-700" onClick={() => setCustModal(true)} data-testid="quick-add-customer-btn"><Plus className="h-3.5 w-3.5" /> New Customer</button>}
              </div>
              <SearchSelectAddNew master="customer" value={f.customerName}
                onChange={(v) => setF((p) => ({ ...p, customerName: v, customerId: "" }))}
                onSelect={(c) => setF((p) => ({ ...p, customerId: c.id, customerName: c.customerName,
                  consigneeAddress: c.consigneeAddress || "", deliveryAddress: c.deliveryAddress || "",
                  gstNumber: c.gstNumber || "", consigneeGstNumber: c.consigneeGstNumber || "" }))}
                testid="oa-customer-select" />
            </div>
            <div>
              <label className="label">O.A. No. (auto-generated & locked)</label>
              <input className="input bg-slate-100 font-mono" value={f.oaNumber || "Auto on save"} disabled data-testid="oa-number-locked" />
            </div>
            <div><label className="label">O.A. Date</label><input type="date" className="input" value={f.oaDate} onChange={(e) => set("oaDate", e.target.value)} data-testid="oa-date-input" /></div>
            <div><label className="label">Consignee's Address</label><textarea className="input h-24 py-2" value={f.consigneeAddress} onChange={(e) => set("consigneeAddress", e.target.value)} data-testid="oa-consignee-address" /></div>
            <div><label className="label">Delivery Address</label><textarea className="input h-24 py-2" value={f.deliveryAddress} onChange={(e) => set("deliveryAddress", e.target.value)} data-testid="oa-delivery-address" /></div>
            <div><label className="label">Party's P.O. No.</label><input className="input" value={f.partyPONumber} onChange={(e) => set("partyPONumber", e.target.value)} data-testid="oa-po-number" /></div>
            <div><label className="label">Party's P.O. Date</label><input type="date" className="input" value={f.partyPODate} onChange={(e) => set("partyPODate", e.target.value)} data-testid="oa-po-date" /></div>
            <div><label className="label">By Mail</label><input className="input" value={f.byMail} onChange={(e) => set("byMail", e.target.value)} data-testid="oa-by-mail" /></div>
            <div><label className="label">GST Number</label><input className="input" value={f.gstNumber} onChange={(e) => set("gstNumber", e.target.value)} data-testid="oa-gst-number" /></div>
            <div><label className="label">Consignee's GST Numbers</label><input className="input" value={f.consigneeGstNumber} onChange={(e) => set("consigneeGstNumber", e.target.value)} data-testid="oa-consignee-gst" /></div>
          </div>
        </div>

        {/* Upload PO */}
        {can("upload_po") && (
          <div className="card p-6" data-testid="upload-po-section">
            <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><Upload className="h-4 w-4 text-amber-600" /> Upload Customer PO</h3>
            {f.poFile ? (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate" data-testid="po-filename">{f.poFile.filename}</div>
                    <div className="text-xs text-slate-400">{Math.round((f.poFile.size || 0) / 1024)} KB</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" className="btn-outline btn-sm" onClick={viewPO} data-testid="view-po-btn">View</button>
                  {!locked && <label className="btn-outline btn-sm cursor-pointer">Replace<input type="file" className="hidden" accept=".pdf,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.gif" onChange={uploadPO} /></label>}
                  {!locked && <button type="button" className="btn-ghost btn-sm text-red-600" onClick={() => set("poFile", null)} data-testid="remove-po-btn"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-amber-400 hover:bg-amber-50/40 transition-colors" data-testid="po-dropzone">
                <Upload className="h-6 w-6 text-slate-400" />
                <span className="text-sm text-slate-600 font-medium">{poBusy ? "Uploading…" : "Click to upload PO"}</span>
                <span className="text-xs text-slate-400">PDF, Excel (xls/xlsx), CSV or image · max 10MB</span>
                <input type="file" className="hidden" accept=".pdf,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.gif" onChange={uploadPO} disabled={poBusy} data-testid="po-file-input" />
              </label>
            )}
          </div>
        )}

        {/* Products */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold text-slate-900">Products</h3>
            <button className="btn-outline btn-sm" onClick={addRow} data-testid="add-product-row"><Plus className="h-4 w-4" /> Add Product</button>
          </div>
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="th w-10">#</th><th className="th">Product Code</th><th className="th">Item</th>
                  <th className="th">Std/Non Std</th><th className="th">Colour</th><th className="th">Qty</th>
                  <th className="th">Unit Rate</th><th className="th">Total</th><th className="th"></th>
                </tr>
              </thead>
              <tbody>
                {f.products.map((p, i) => (
                  <tr key={i} data-testid={`product-row-${i}`}>
                    <td className="td">{i + 1}</td>
                    <td className="td">
                      <SearchSelectAddNew master="product" value={p.productCode}
                        onChange={(v) => setRow(i, "productCode", v)}
                        onSelect={(pr) => setF((prev) => { const rows = [...prev.products]; rows[i] = { ...rows[i], productCode: pr.productCode, item: pr.itemName, stdNonStd: pr.stdNonStd || "Std", colour: pr.colour || "", unitRate: pr.defaultUnitRate || 0 }; return { ...prev, products: rows }; })}
                        testid={`product-select-${i}`} />
                    </td>
                    <td className="td"><input className="input h-9 min-w-[160px]" value={p.item} onChange={(e) => setRow(i, "item", e.target.value)} /></td>
                    <td className="td">
                      <select className="input h-9" value={p.stdNonStd} onChange={(e) => setRow(i, "stdNonStd", e.target.value)}>
                        <option>Std</option><option>Non Std</option>
                      </select>
                    </td>
                    <td className="td"><input className="input h-9 w-24" value={p.colour} onChange={(e) => setRow(i, "colour", e.target.value)} /></td>
                    <td className="td"><input type="number" min="0" className="input h-9 w-20" value={p.qty} onChange={(e) => setRow(i, "qty", e.target.value)} data-testid={`product-qty-${i}`} /></td>
                    <td className="td"><input type="number" min="0" className="input h-9 w-28" value={p.unitRate} onChange={(e) => setRow(i, "unitRate", e.target.value)} data-testid={`product-rate-${i}`} /></td>
                    <td className="td font-semibold whitespace-nowrap">{inr((Number(p.qty) || 0) * (Number(p.unitRate) || 0))}</td>
                    <td className="td">
                      {f.products.length > 1 && <button onClick={() => removeRow(i)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" data-testid={`remove-product-${i}`}><Trash2 className="h-4 w-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300">
                  <td className="td font-head font-bold text-right" colSpan={7}>NET TOTAL</td>
                  <td className="td font-head font-bold text-lg" data-testid="oa-net-total">{inr(net)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Terms */}
        <div className="card p-6">
          <h3 className="font-head font-semibold text-slate-900 mb-4">Terms &amp; Conditions</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              ["price", "Price"], ["deliveryPeriod", "Delivery Period"], ["payment", "Payment"], ["roadPermitNo", "Road Permit No."],
              ["cgst", "CGST"], ["insurance", "Insurance"], ["sgst", "SGST"], ["fright", "Fright"], ["igst", "IGST"], ["installation", "Installation"],
            ].map(([k, lbl]) => (
              <div key={k}><label className="label">{lbl}</label><input className="input" value={f.terms[k]} onChange={(e) => setTerm(k, e.target.value)} data-testid={`term-${k}`} /></div>
            ))}
            <div className="md:col-span-2"><label className="label">Any other conditions</label><textarea className="input h-20 py-2" value={f.terms.otherConditions} onChange={(e) => setTerm("otherConditions", e.target.value)} /></div>
          </div>
        </div>

        {/* Special instruction + authorization */}
        <div className="card p-6">
          <label className="label">Special Instruction</label>
          <textarea className="input h-24 py-2 mb-4" value={f.specialInstruction} onChange={(e) => set("specialInstruction", e.target.value)} data-testid="oa-special-instruction" />
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="label">Prepared By</label><input className="input" value={f.preparedBy} onChange={(e) => set("preparedBy", e.target.value)} /></div>
            <div><label className="label">Authorized Sign</label><input className="input" value={f.authorizedSign} onChange={(e) => set("authorizedSign", e.target.value)} /></div>
            <div><label className="label">Destination</label>
              <SearchSelectAddNew master="destination" value={f.destination}
                onChange={(v) => set("destination", v)} onSelect={(r) => set("destination", r.name)}
                testid="oa-destination" /></div>
            <div><label className="label">Delivered Details</label><input className="input" value={f.deliveredDetails} onChange={(e) => set("deliveredDetails", e.target.value)} /></div>
            <div><label className="label">Date</label><input type="date" className="input" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div><label className="label">Bill No.</label><input className="input" value={f.billNumber} onChange={(e) => set("billNumber", e.target.value)} /></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <span className="label mb-0 mr-2">CC to:</span>
            {[["ccDealer", "Dealer"], ["ccAccount", "Account Dept."], ["ccMarketing", "Marketing Dept."]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="h-4 w-4 accent-amber-600" /> {lbl}
              </label>
            ))}
          </div>
        </div>
      </div>

      {custModal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setCustModal(false)}>
          <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quick-customer-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold">Add Customer</h3>
              <button onClick={() => setCustModal(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="label">Customer Name *</label><input className="input" value={custForm.customerName} onChange={(e) => setCustForm({ ...custForm, customerName: e.target.value })} data-testid="quick-customer-name" /></div>
              <div><label className="label">Consignee's Address</label><textarea className="input h-20 py-2" value={custForm.consigneeAddress} onChange={(e) => setCustForm({ ...custForm, consigneeAddress: e.target.value })} /></div>
              <div><label className="label">Delivery Address</label><textarea className="input h-20 py-2" value={custForm.deliveryAddress} onChange={(e) => setCustForm({ ...custForm, deliveryAddress: e.target.value })} /></div>
              <div><label className="label">GST Number</label><input className="input" value={custForm.gstNumber} onChange={(e) => setCustForm({ ...custForm, gstNumber: e.target.value })} /></div>
              <div><label className="label">Consignee's GST Number</label><input className="input" value={custForm.consigneeGstNumber} onChange={(e) => setCustForm({ ...custForm, consigneeGstNumber: e.target.value })} /></div>
              <div><label className="label">Contact Person</label><input className="input" value={custForm.contactPerson} onChange={(e) => setCustForm({ ...custForm, contactPerson: e.target.value })} /></div>
              <div><label className="label">Mobile</label><input className="input" value={custForm.mobile} onChange={(e) => setCustForm({ ...custForm, mobile: e.target.value })} /></div>
              <div><label className="label">Email</label><input className="input" value={custForm.email} onChange={(e) => setCustForm({ ...custForm, email: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-outline" onClick={() => setCustModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={saveCustomer} data-testid="save-quick-customer-btn">Save Customer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
